import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, MessageSquare, FolderOpen, Receipt, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { PortalHeader, fmtDate } from "@/pages/client/_shared";

function StatCard({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} data-testid={`client-stat-${label.toLowerCase().split(" ")[0]}`}
      className="fv-card-hover rounded-2xl border border-white/8 bg-[#121214] p-5 block">
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-[#D4AF37]" /></div>
      <div className="text-2xl font-heading text-white">{value}</div>
      <div className="text-sm text-[#71717A] mt-0.5">{label}</div>
    </Link>
  );
}

export default function ClientDashboard() {
  const { client } = useClientAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const p = data?.progress;
  return (
    <div className="fv-fade-up">
      <PortalHeader title={`Welcome, ${client?.name?.split(" ")[0] || "there"}`}
        subtitle="Your private workspace to track progress, message your advisor, and manage everything in one place." />

      <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6" data-testid="client-progress-banner">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-[#D4AF37] uppercase tracking-wider">Onboarding progress</div>
          <div className="text-2xl font-heading text-white">{p?.pct ?? 0}%</div>
        </div>
        <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
          <div className="h-full fv-gold-gradient transition-all duration-700" style={{ width: `${p?.pct ?? 0}%` }} />
        </div>
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-[#A1A1AA]">{p?.done ?? 0} of {p?.total ?? 0} milestones complete</span>
          <Link to="/client/progress" className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1">View progress <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={MessageSquare} label="Unread messages" value={data?.unread_messages ?? 0} to="/client/messages" />
        <StatCard icon={FolderOpen} label="Documents" value={data?.documents ?? 0} to="/client/documents" />
        <StatCard icon={Receipt} label="Open invoices" value={data?.open_invoices ?? 0} to="/client/billing" />
        <StatCard icon={TrendingUp} label="Notifications" value={data?.unread_notifications ?? 0} to="/client/notifications" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Link to="/client/advisor" data-testid="client-advisor-cta"
          className="fv-card-hover lg:col-span-2 rounded-2xl border border-white/8 bg-[#121214] p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-[#0A0A0B]" /></div>
          <div><div className="text-white font-medium">Ask your AI Advisor</div>
            <p className="text-sm text-[#71717A]">Get personalized guidance tailored to {client?.company || "your business"}.</p></div>
          <ArrowRight className="w-5 h-5 text-[#D4AF37] ml-auto" />
        </Link>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6">
          <div className="text-sm text-white font-medium mb-3">Recent activity</div>
          {(data?.recent_activity || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">Nothing yet.</p>
          ) : (
            <ul className="space-y-3">{data.recent_activity.map((n) => (
              <li key={n.notif_id} className="text-sm"><div className="text-[#D4D4D8]">{n.title}</div>
                <div className="text-xs text-[#52525B]">{fmtDate(n.created_at)}</div></li>))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
